// Shared entry for the journey chapters. Pages declare mounts with
// data-component / data-stat / data-media / data-scrubber / data-illustration;
// this script fills them from the validated data.

import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";
import "./styles/reading.css";
import "./styles/network.css";

import { renderBars } from "./charts/bars";
import { renderOutcomeMatrix } from "./charts/outcome-matrix";
import { renderPairedEffect } from "./charts/paired-effect";
import { markActiveNav } from "./components/chrome";
import { renderHashDemo } from "./components/hash-demo";
import { mountMedia } from "./components/media-player";
import { mountNetworkFlows } from "./components/network-flow";
import { renderResultPanel } from "./components/result-panel";
import { mountScrubbers } from "./components/scrubber";
import { renderSliceExplainer } from "./components/slice-explainer";
import { bindStats } from "./components/stats";
import { renderThreeArm } from "./components/three-arm";
import { loadJourney, loadStudy, renderUnavailable } from "./data/load";
import type { ExperimentSummary, JourneyData, SiteStatus } from "./data/schema";

markActiveNav();

function mountCharts(
  journey: JourneyData,
  summary: ExperimentSummary,
  status: SiteStatus,
): void {
  for (const mount of document.querySelectorAll<HTMLElement>(
    "[data-component]",
  )) {
    switch (mount.dataset.component) {
      case "spectrum-bars": {
        const spectrum = journey.lab03.spectrum;
        renderBars(
          mount,
          [
            {
              label: "random actions",
              value: spectrum.random?.success_rate ?? 0,
              color: "var(--base-method)",
              detail: `${Math.round(spectrum.random?.mean_steps ?? 0)} steps on average`,
            },
            {
              label: "wall follower",
              value: spectrum.wall_follower?.success_rate ?? 0,
              color: "var(--caution)",
              detail: "follows the wall on its right",
            },
            {
              label: "scripted expert",
              value: spectrum.oracle?.success_rate ?? 0,
              color: "var(--recovery)",
              detail: `${(spectrum.oracle?.mean_steps ?? 0).toFixed(1)} steps on average`,
            },
          ],
          {
            title: `Success on ${spectrum.oracle?.episodes ?? 0} shared scenarios`,
            description:
              "Success rates of random actions, wall following, and the expert.",
          },
        );
        break;
      }
      case "imitation-bars": {
        renderBars(
          mount,
          [
            {
              label: "without memory",
              value: journey.lab04.memoryless.unseen_success_mean,
              color: "var(--base-method)",
              dots: journey.lab04.memoryless.unseen_success_per_seed,
              detail: "uses only the current observation",
            },
            {
              label: "with memory",
              value: journey.lab04.recurrent.unseen_success_mean,
              color: "var(--recovery)",
              dots: journey.lab04.recurrent.unseen_success_per_seed,
              detail: "also uses information from earlier steps",
            },
          ],
          {
            title:
              "Success on new mazes. Bars are averages; dots are the three training runs.",
            description: "Success on new mazes, with and without memory.",
          },
        );
        break;
      }
      case "ablation-bars": {
        renderBars(
          mount,
          journey.lab05.variants.map((variant) => ({
            label: variant.name.replace(/_/g, " "),
            value: variant.unseen_success_mean,
            color:
              variant.name === "full"
                ? "var(--recovery)"
                : "var(--base-method)",
            dots: variant.unseen_success_per_seed,
            detail: `${new Intl.NumberFormat("en-US").format(variant.parameters)} parameters`,
          })),
          {
            title:
              "Success after changing one component. Dots show the three training runs.",
            description:
              "Success on new mazes for each version of the network.",
          },
        );
        break;
      }
      case "mini-arms-bars": {
        const matrix = journey.lab06.success_matrix;
        renderBars(
          mount,
          [
            {
              label: "original agent",
              value: matrix.base?.unseen ?? 0,
              color: "var(--base-method)",
            },
            {
              label: "extra demonstrations",
              value: matrix.extra?.unseen ?? 0,
              color: "var(--extra)",
            },
            {
              label: "recovery labels",
              value: matrix.recovery?.unseen ?? 0,
              color: "var(--recovery)",
            },
          ],
          {
            title:
              "Success with a new action change. All assigned episodes count.",
            description:
              "Success of the original agent and the two further-trained agents.",
          },
        );
        break;
      }
      case "paired-effect":
        if (status.result_release) renderPairedEffect(mount, summary);
        break;
      case "outcome-matrix":
        if (status.result_release) renderOutcomeMatrix(mount, summary);
        break;
      case "three-arm":
        renderThreeArm(mount, summary);
        break;
      case "result-panel":
        renderResultPanel(mount, status, summary);
        break;
      case "slice-explainer":
        renderSliceExplainer(mount);
        break;
      case "hash-demo":
        renderHashDemo(mount);
        break;
      default:
        console.error(`unknown component: ${mount.dataset.component}`);
    }
  }
}

void (async () => {
  try {
    const [{ status, summary }, journey] = await Promise.all([
      loadStudy(),
      loadJourney(),
    ]);
    bindStats({ summary, journey });
    mountCharts(journey, summary, status);
    for (const chart of document.querySelectorAll<HTMLElement>(".chart")) {
      chart.tabIndex = 0;
      chart.setAttribute("role", "region");
      chart.setAttribute(
        "aria-label",
        chart.querySelector("title")?.textContent ?? "Scrollable chart",
      );
    }
    mountMedia(journey.media);
    await mountScrubbers();
    await mountNetworkFlows();
  } catch (error) {
    console.error(error);
    const main = document.querySelector<HTMLElement>("main article");
    if (main) {
      const box = document.createElement("div");
      renderUnavailable(box, error);
      main.prepend(box);
    }
  }
})();
